import { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/auth'
import { readSSEStream } from '../utils/sse-reader'
import type { T2ICard, I2VResponse } from '../types/director_v2'
import { STYLE_CATEGORIES } from '../components/director_v2/styleOptions'

const API_BASE = 'https://cloudflare-mutimodal-backend.steven-mei0814.workers.dev'

export function useDirectorStateV2() {
  const { token } = useAuthStore()

  // ModuleA state
  const [seedPrompt, setSeedPrompt] = useState('')
  const [styleSelections, setStyleSelections] = useState<Record<string, string>>({})
  const [imageCount, setImageCount] = useState(5)
  const [isGeneratingT2I, setIsGeneratingT2I] = useState(false)

  const setStyleSelection = (key: string, value: string) => {
    setStyleSelections((prev) => ({ ...prev, [key]: value }))
  }

  const buildStylePreference = (): string => {
    const parts = STYLE_CATEGORIES
      .map((cat) => styleSelections[cat.key])
      .filter((v) => v && v.trim())
    return parts.join(', ')
  }

  // ModuleB state
  const [cards, setCards] = useState<T2ICard[]>([])
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null)
  const [recordId, setRecordId] = useState<string>('')

  // ModuleC state
  const [videoRequirement, setVideoRequirement] = useState('')
  const [durationMode, setDurationMode] = useState<'10s' | '30s'>('10s')
  const [isGeneratingI2V, setIsGeneratingI2V] = useState(false)
  const [cameraMovements, setCameraMovements] = useState<string[]>([])
  const [rhythm, setRhythm] = useState('')

  // ModuleD state
  const [i2vResults, setI2vResults] = useState<Map<number, I2VResponse>>(new Map())

  // Preview images state
  const [previewImages, setPreviewImages] = useState<Map<number, { url: string; loading: boolean }>>(new Map())

  // Batch preview state
  const [batchPreviewMode, setBatchPreviewMode] = useState(false)
  const [batchGenerating, setBatchGenerating] = useState(false)

  // Modal state
  const [previewModalImage, setPreviewModalImage] = useState<{ url: string; prompt: string } | null>(null)

  // ESC key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && previewModalImage) {
        setPreviewModalImage(null)
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [previewModalImage])

  // Load latest record on mount
  useEffect(() => {
    const loadLatestRecord = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/director_v2/history?page=1&limit=1`, {
          headers: { Authorization: `Bearer ${token}` },
        })

        if (!response.ok) return

        const result = await response.json()
        if (!result.success || !result.data?.records?.length) return

        const record = result.data.records[0]

        setSeedPrompt(record.seed_prompt || '')

        if (record.t2i_results) {
          try {
            const t2iData = typeof record.t2i_results === 'string'
              ? JSON.parse(record.t2i_results)
              : record.t2i_results

            if (t2iData.results && Array.isArray(t2iData.results)) {
              setCards(t2iData.results)
              const count = t2iData.results.length
              if ([1, 3, 5, 10].includes(count)) {
                setImageCount(count)
              }
            }
          } catch (e) {
            console.error('Failed to parse t2i_results:', e)
          }
        }

        setRecordId(record.id)

        if (record.selected_card_id && record.i2v_results) {
          setSelectedCardId(record.selected_card_id)
          try {
            const i2vData = typeof record.i2v_results === 'string'
              ? JSON.parse(record.i2v_results)
              : record.i2v_results
            if (i2vData) {
              setI2vResults(new Map([[record.selected_card_id, i2vData]]))
            }
          } catch (e) {
            console.error('Failed to parse i2v_results:', e)
          }
        }
      } catch (error) {
        console.error('Failed to load latest record:', error)
      }
    }

    if (token) {
      loadLatestRecord()
    }
  }, [token])

  // Computed values
  const selectedCard = cards.find((c) => c.id === selectedCardId) || null
  const currentI2vResult = selectedCardId ? i2vResults.get(selectedCardId) || null : null

  // Generate T2I cards
  const handleGenerateT2I = async () => {
    if (!seedPrompt.trim()) {
      alert('请输入种子需求')
      return
    }

    setIsGeneratingT2I(true)
    setCards([])
    setSelectedCardId(null)
    setI2vResults(new Map())
    setPreviewImages(new Map())
    setVideoRequirement('')
    setRecordId('')
    setBatchPreviewMode(false)
    setPreviewModalImage(null)

    try {
      const stylePreference = buildStylePreference()
      const response = await fetch(`${API_BASE}/api/director_v2/generate-t2i`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          seed_prompt: seedPrompt,
          style_preference: stylePreference || null,
          count: imageCount,
        }),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      await readSSEStream<{ results: T2ICard[] }>({
        response,
        isValidPartial: (p): p is { results: T2ICard[] } =>
          !!(p?.results && Array.isArray(p.results)),
        onPartialResult: (_accumulated, parsed) => {
          setCards([...parsed.results])
        },
        onComplete: (data) => {
          setRecordId(data.record_id || '')
          setIsGeneratingT2I(false)
        },
        onError: (error) => { throw error },
      })
    } catch (error) {
      console.error('Generate T2I error:', error)
      alert('生成失败: ' + (error as Error).message)
    } finally {
      setIsGeneratingT2I(false)
    }
  }

  // New creation
  const handleNewCreation = () => {
    setSeedPrompt('')
    setStyleSelections({})
    setImageCount(5)
    setCards([])
    setSelectedCardId(null)
    setVideoRequirement('')
    setCameraMovements([])
    setRhythm('')
    setI2vResults(new Map())
    setRecordId('')
    setPreviewImages(new Map())
    setPreviewModalImage(null)
  }

  // Generate preview image for a single card
  const handleGeneratePreview = async (card: T2ICard, event: React.MouseEvent) => {
    event.stopPropagation()

    const existingPreview = previewImages.get(card.id)
    if (existingPreview && existingPreview.url) {
      setPreviewModalImage({ url: existingPreview.url, prompt: card.prompt })
      return
    }

    setPreviewImages((prev) => {
      const newMap = new Map(prev)
      newMap.set(card.id, { url: '', loading: true })
      return newMap
    })

    try {
      const response = await fetch(`${API_BASE}/api/director_v2/generate-preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ prompt: card.prompt_en || card.prompt }),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()

      if (result.success && result.url) {
        setPreviewImages((prev) => {
          const newMap = new Map(prev)
          newMap.set(card.id, { url: result.url, loading: false })
          return newMap
        })
        setPreviewModalImage({ url: result.url, prompt: card.prompt })
      } else {
        throw new Error(result.error || '生成失败')
      }
    } catch (error) {
      console.error('Generate preview error:', error)
      alert('生成预览图失败: ' + (error as Error).message)
      setPreviewImages((prev) => {
        const newMap = new Map(prev)
        newMap.delete(card.id)
        return newMap
      })
    }
  }

  // Batch preview
  const handleBatchPreview = async () => {
    if (batchPreviewMode) {
      setBatchPreviewMode(false)
      return
    }

    setBatchPreviewMode(true)
    setBatchGenerating(true)

    for (const card of cards) {
      const existingPreview = previewImages.get(card.id)
      if (existingPreview && existingPreview.url) continue

      setPreviewImages((prev) => {
        const newMap = new Map(prev)
        newMap.set(card.id, { url: '', loading: true })
        return newMap
      })

      try {
        const response = await fetch(`${API_BASE}/api/director_v2/generate-preview`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ prompt: card.prompt_en || card.prompt }),
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()

        if (result.success && result.url) {
          setPreviewImages((prev) => {
            const newMap = new Map(prev)
            newMap.set(card.id, { url: result.url, loading: false })
            return newMap
          })
        } else {
          throw new Error(result.error || '生成失败')
        }
      } catch (error) {
        console.error('Generate preview error for card', card.id, error)
        setPreviewImages((prev) => {
          const newMap = new Map(prev)
          newMap.delete(card.id)
          return newMap
        })
      }
    }

    setBatchGenerating(false)
  }

  // Generate I2V storyboard
  const handleGenerateI2V = async () => {
    if (!recordId) { alert('请先生成T2I卡片'); return }
    if (!selectedCard) { alert('请先点击选择一张卡片'); return }
    if (!videoRequirement.trim()) { alert('请输入视频剧情需求'); return }

    setIsGeneratingI2V(true)

    try {
      const response = await fetch(`${API_BASE}/api/director_v2/generate-i2v`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          record_id: recordId,
          card_id: selectedCard.id,
          card_prompt: selectedCard.prompt,
          video_requirement: videoRequirement,
          duration_mode: durationMode,
          camera_movements: cameraMovements.length > 0 ? cameraMovements : undefined,
          rhythm: rhythm || undefined,
        }),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const cardId = selectedCard.id
      await readSSEStream<I2VResponse>({
        response,
        isValidPartial: (p): p is I2VResponse =>
          !!(p?.director_analysis && p?.storyboard),
        onPartialResult: (_accumulated, parsed) => {
          setI2vResults((prev) => {
            const newMap = new Map(prev)
            newMap.set(cardId, parsed)
            return newMap
          })
        },
        onComplete: (data) => {
          if (data.i2v_results) {
            setI2vResults((prev) => {
              const newMap = new Map(prev)
              newMap.set(cardId, data.i2v_results as I2VResponse)
              return newMap
            })
          }
          setIsGeneratingI2V(false)
        },
        onError: (error) => { throw error },
      })
    } catch (error) {
      console.error('Generate I2V error:', error)
      alert('生成失败: ' + (error as Error).message)
    } finally {
      setIsGeneratingI2V(false)
    }
  }

  const handleOpenPreviewModal = (url: string, prompt: string) => {
    setPreviewModalImage({ url, prompt })
  }

  // Variant generation state
  const [variantTargetCardId, setVariantTargetCardId] = useState<number | null>(null)
  const [variantModification, setVariantModification] = useState('')
  const [isGeneratingVariant, setIsGeneratingVariant] = useState(false)

  const handleGenerateVariant = async () => {
    const targetCard = cards.find((c) => c.id === variantTargetCardId)
    if (!targetCard || !variantModification.trim()) return

    setIsGeneratingVariant(true)

    try {
      const response = await fetch(`${API_BASE}/api/director_v2/generate-variant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          original_prompt: targetCard.prompt,
          modification: variantModification,
        }),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      await readSSEStream<{ results: T2ICard[] }>({
        response,
        isValidPartial: (p): p is { results: T2ICard[] } =>
          !!(p?.results && Array.isArray(p.results)),
        onPartialResult: () => {},
        onComplete: (data) => {
          if (data.variants && Array.isArray(data.variants)) {
            const maxId = Math.max(...cards.map((c) => c.id), 0)
            const newVariants = (data.variants as T2ICard[]).map((v, i) => ({
              ...v,
              id: maxId + i + 1,
            }))
            setCards((prev) => [...prev, ...newVariants])
          }
          setVariantTargetCardId(null)
          setVariantModification('')
          setIsGeneratingVariant(false)
        },
        onError: (error) => { throw error },
      })
    } catch (error) {
      console.error('Generate variant error:', error)
      alert('变体生成失败: ' + (error as Error).message)
    } finally {
      setIsGeneratingVariant(false)
    }
  }

  // Video prompt refine state
  const [videoRefineInput, setVideoRefineInput] = useState('')
  const [isRefiningVideo, setIsRefiningVideo] = useState(false)

  const handleRefineVideoPrompt = async (originalVideoPrompt: string) => {
    if (!videoRefineInput.trim() || !selectedCardId) return

    setIsRefiningVideo(true)

    try {
      const response = await fetch(`${API_BASE}/api/director_v2/refine-video-prompt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          original_video_prompt: originalVideoPrompt,
          modification: videoRefineInput,
        }),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const cardId = selectedCardId
      await readSSEStream<{ action_summary: string; video_prompt: string }>({
        response,
        isValidPartial: (p): p is { action_summary: string; video_prompt: string } =>
          !!(p?.video_prompt),
        onPartialResult: () => {},
        onComplete: (data) => {
          if (data.refined) {
            const refined = data.refined as { action_summary: string; video_prompt: string }
            setI2vResults((prev) => {
              const existing = prev.get(cardId)
              if (!existing) return prev
              const newMap = new Map(prev)
              newMap.set(cardId, {
                ...existing,
                storyboard: {
                  ...existing.storyboard,
                  part_1_0_to_10s: {
                    action_summary: refined.action_summary || existing.storyboard.part_1_0_to_10s.action_summary,
                    video_prompt: refined.video_prompt,
                  },
                },
              })
              return newMap
            })
          }
          setVideoRefineInput('')
          setIsRefiningVideo(false)
        },
        onError: (error) => { throw error },
      })
    } catch (error) {
      console.error('Refine video prompt error:', error)
      alert('提示词优化失败: ' + (error as Error).message)
    } finally {
      setIsRefiningVideo(false)
    }
  }

  return {
    // State
    seedPrompt, setSeedPrompt,
    styleSelections, setStyleSelection,
    imageCount, setImageCount,
    isGeneratingT2I,
    cards,
    selectedCardId, setSelectedCardId,
    videoRequirement, setVideoRequirement,
    durationMode, setDurationMode,
    cameraMovements, setCameraMovements,
    rhythm, setRhythm,
    isGeneratingI2V,
    selectedCard,
    currentI2vResult,
    previewImages,
    batchPreviewMode,
    batchGenerating,
    previewModalImage, setPreviewModalImage,
    // Variant
    variantTargetCardId, setVariantTargetCardId,
    variantModification, setVariantModification,
    isGeneratingVariant,
    // Video refine
    videoRefineInput, setVideoRefineInput,
    isRefiningVideo,
    // Handlers
    handleGenerateT2I,
    handleNewCreation,
    handleGeneratePreview,
    handleBatchPreview,
    handleGenerateI2V,
    handleOpenPreviewModal,
    handleGenerateVariant,
    handleRefineVideoPrompt,
  }
}
